import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BookingState } from "@/types/bookinghome";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

interface PaymentSummaryProps {
  bookingState: BookingState;
  total: number;
}

export default function PaymentSummary({ bookingState, total }: PaymentSummaryProps) {
  const { t } = useTranslation('business', { keyPrefix: 'payment.summary' });

  const { selectedTimeSlot, selectedDate, participants } = bookingState;

  // Format date and time safely
  const formattedDate = selectedDate ? 
    (selectedDate instanceof Date ? format(selectedDate, 'PPP') : format(new Date(selectedDate), 'PPP')) : '';
  const formattedStartTime = selectedTimeSlot?.startTime ? 
    (selectedTimeSlot.startTime.includes('T') ? format(parseISO(selectedTimeSlot.startTime), 'p') : selectedTimeSlot.startTime) : '';
  const formattedEndTime = selectedTimeSlot?.endTime ? 
    (selectedTimeSlot.endTime.includes('T') ? format(parseISO(selectedTimeSlot.endTime), 'p') : selectedTimeSlot.endTime) : '';

  // Calculate prices safely
  const adultPrice = selectedTimeSlot?.price?.adult?.amount || 0;
  const childPrice = selectedTimeSlot?.price?.child?.amount || 0;
  const studentPrice = selectedTimeSlot?.price?.student?.amount || 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Date and Time */}
          <div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('date')}</span>
              <span>{formattedDate}</span>
            </div>
            {formattedStartTime && formattedEndTime && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-500">{t('time')}</span>
                <span>{formattedStartTime} - {formattedEndTime}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Participants */}
          <div className="space-y-1">
            {participants.adults > 0 && (
              <div className="flex justify-between text-sm">
                <span>{t('participants.adults', { count: participants.adults })}</span>
                <span>${adultPrice * participants.adults}</span>
              </div>
            )}
            {participants.children > 0 && (
              <div className="flex justify-between text-sm">
                <span>{t('participants.children', { count: participants.children })}</span>
                <span>${childPrice * participants.children}</span>
              </div>
            )}
            {participants.students > 0 && (
              <div className="flex justify-between text-sm">
                <span>{t('participants.students', { count: participants.students })}</span>
                <span>${studentPrice * participants.students}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Total */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('pricing.subtotal')}</span>
              <span>${total}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('pricing.tax')}</span>
              <span>${(total * 0.1).toFixed(2)}</span>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between font-semibold">
              <span>{t('pricing.total')}</span>
              <span>${(total * 1.1).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}